import { c as _c } from "react/compiler-runtime";
import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { DateCell } from "@components/common/date-cell";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { Text } from "@flat-ui";
import { useMembers } from "@hooks/use-members";
import { useHandleColumnOrderChange } from "@hooks/use-handle-column-order-change";
import { Box } from "@mui-wrapper";
import { MarkupDueDate } from "@pages/project-details/project-markups/due-date/markup-due-date";
import { MarkupActions } from "@pages/project-details/project-markups/markup-actions";
import { MarkupAssignee } from "@pages/project-details/project-markups/markup-assignee";
import { MarkupAttachments } from "@pages/project-details/project-markups/markup-attachments";
import { MarkupCreator } from "@pages/project-details/project-markups/markup-creator";
import { getMarkupCreatorName } from "@pages/project-details/project-markups/markup-details-utils";
import { MarkupNameAndDetails } from "@pages/project-details/project-markups/markup-name-and-details";
import { useMarkupsSortQueryParam } from "@pages/project-details/project-markups/markups-toolbar/use-markups-sort-query-param";
import { MarkupTags } from "@pages/project-details/project-markups/markup-tags";
import { getShownMarkupsColumns, getVisibleColumnMove, ProjectMarkupsTableHeaders } from "@pages/project-details/project-markups/project-markups-table-utils";
import { getMarkupLastUpdate, getSetMarkupField } from "@pages/project-details/project-markups/sidepanel/markup-sidepanel-activity-utils";
import { MarkupStatusDropdown } from "@pages/project-details/project-markups/status/markup-status-dropdown";
import { getMarkupStatusValue } from "@pages/project-details/project-markups/status/markup-status-utils";
import { TableType } from "@utils/track-event/track-event-list";
import { useSearchParams } from "react-router-dom";

/** Header of a column that can be dragged to reorder it */
function renderDraggableHeader(params) {
  return <TableHeaderWithDraggableIcon title={params.colDef.field} />;
}

/** Column showing a date of the markup, or a dash when it has none */
function getDateColumn(field, getDate) {
  return {
    field,
    minWidth: 140,
    flex: 0.5,
    type: "date",
    renderHeader: renderDraggableHeader,
    renderCell: params => {
      const date = getDate(params.row.entity);
      return <Text variant="bodyS" component="div" sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        px: 1
      }}>
          {date ? <DateCell date={date} /> : "-"}
        </Text>;
    },
    valueGetter: (_, row) => {
      const date = getDate(row.entity);
      return date ? new Date(date) : undefined;
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
    sortable: true
  };
}

/** Returns the columns of the project markups table the user chose to show, in their order */
export function useProjectMarkupsColumns() {
  const $ = _c(13);
  const {
    companyMembers,
    projectMembers
  } = useMembers();
  const {
    columnPreferences,
    visibleOrderedColumns
  } = useColumnPreferencesContext();
  const storeColumnOrderChange = useHandleColumnOrderChange(TableType.projectMarkups);
  const [searchParams] = useSearchParams();
  const [sortItem] = useMarkupsSortQueryParam();
  let t0;
  if ($[0] !== columnPreferences || $[1] !== companyMembers || $[2] !== projectMembers || $[3] !== searchParams || $[4] !== sortItem.field || $[5] !== storeColumnOrderChange || $[6] !== visibleOrderedColumns) {
    const forcedVisibleColumns = [...(searchParams.has(ProjectMarkupsTableHeaders.creator) ? [ProjectMarkupsTableHeaders.creator] : []), ...(visibleOrderedColumns.includes(sortItem.field) ? [] : [sortItem.field])];
    const shownColumns = getShownMarkupsColumns(visibleOrderedColumns, forcedVisibleColumns);
    const onColumnOrderChange = function onColumnOrderChange(params) {
      const move = getVisibleColumnMove(shownColumns, visibleOrderedColumns, params);
      if (move) {
        storeColumnOrderChange({
          ...params,
          ...move
        });
      }
    };
    let t1;
    if ($[8] !== companyMembers || $[9] !== projectMembers) {
      t1 = (__1, row_1) => getMarkupCreatorName({
        creatorId: row_1.entity.createdBy,
        companyMembers,
        projectMembers
      });
      $[8] = companyMembers;
      $[9] = projectMembers;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    let t2;
    if ($[11] !== columnPreferences) {
      t2 = () => <FaroTableColumnVisibilityMenuIcon columnPreferences={columnPreferences} />;
      $[11] = columnPreferences;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    const columns = {
      [ProjectMarkupsTableHeaders.name]: {
        field: ProjectMarkupsTableHeaders.name,
        minWidth: 200,
        flex: 1.25,
        disableReorder: true,
        renderCell: _temp,
        valueGetter: _temp2,
        sortable: true
      },
      [ProjectMarkupsTableHeaders.assignee]: {
        field: ProjectMarkupsTableHeaders.assignee,
        minWidth: 180,
        flex: 1,
        renderHeader: renderDraggableHeader,
        renderCell: _temp3,
        valueGetter: _temp4,
        sortable: true
      },
      [ProjectMarkupsTableHeaders.creator]: {
        field: ProjectMarkupsTableHeaders.creator,
        minWidth: 180,
        flex: 1,
        renderHeader: renderDraggableHeader,
        renderCell: _temp5,
        valueGetter: t1,
        sortable: true
      },
      [ProjectMarkupsTableHeaders.dueDate]: {
        field: ProjectMarkupsTableHeaders.dueDate,
        minWidth: 140,
        flex: 0.5,
        type: "date",
        renderHeader: renderDraggableHeader,
        renderCell: _temp6,
        valueGetter: _temp7,
        sortable: true
      },
      [ProjectMarkupsTableHeaders.status]: {
        field: ProjectMarkupsTableHeaders.status,
        minWidth: 140,
        flex: 0.5,
        renderHeader: renderDraggableHeader,
        renderCell: _temp8,
        valueGetter: _temp9,
        sortable: true
      },
      [ProjectMarkupsTableHeaders.tags]: {
        field: ProjectMarkupsTableHeaders.tags,
        minWidth: 160,
        flex: 0.75,
        renderHeader: renderDraggableHeader,
        renderCell: _temp0,
        valueGetter: _temp10,
        sortable: true
      },
      [ProjectMarkupsTableHeaders.attachments]: {
        field: ProjectMarkupsTableHeaders.attachments,
        headerAlign: "left",
        minWidth: 160,
        flex: 0.75,
        type: "number",
        align: "left",
        renderHeader: renderDraggableHeader,
        renderCell: _temp11,
        valueGetter: _temp12,
        sortable: true
      },
      [ProjectMarkupsTableHeaders.createdAt]: getDateColumn(ProjectMarkupsTableHeaders.createdAt, _temp13),
      [ProjectMarkupsTableHeaders.lastUpdatedAt]: getDateColumn(ProjectMarkupsTableHeaders.lastUpdatedAt, _temp14),
      [ProjectMarkupsTableHeaders.assignedAt]: getDateColumn(ProjectMarkupsTableHeaders.assignedAt, _temp15),
      [ProjectMarkupsTableHeaders.dueDateSetAt]: getDateColumn(ProjectMarkupsTableHeaders.dueDateSetAt, _temp16),
      [ProjectMarkupsTableHeaders.options]: {
        field: ProjectMarkupsTableHeaders.options,
        type: "actions",
        align: "right",
        width: 90,
        disableReorder: true,
        renderHeader: t2,
        renderCell: _temp17
      }
    };
    t0 = {
      columns: shownColumns.map(column => columns[column]),
      onColumnOrderChange,
      forcedVisibleColumns
    };
    $[0] = columnPreferences;
    $[1] = companyMembers;
    $[2] = projectMembers;
    $[3] = searchParams;
    $[4] = sortItem.field;
    $[5] = storeColumnOrderChange;
    $[6] = visibleOrderedColumns;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return t0;
}
function _temp17(params_7) {
  return <Box component="div" sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "right"
  }}><MarkupActions markup={params_7.row.entity} /></Box>;
}
function _temp16(markup_2) {
  return getSetMarkupField(markup_2, "dueDate")?.modifiedAt;
}
function _temp15(markup_1) {
  return getSetMarkupField(markup_1, "assignee")?.modifiedAt;
}
function _temp14(markup_0) {
  return getMarkupLastUpdate(markup_0).modifiedAt;
}
function _temp13(markup) {
  return markup.createdAt;
}
function _temp12(__5, row_5) {
  return row_5.entity.attachmentsCount;
}
function _temp11(params_6) {
  return <MarkupAttachments markup={params_6.row.entity} />;
}
function _temp10(__4, row_4) {
  if (row_4.entity.labels?.length) {
    return row_4.entity.labels.map(_temp1).join(", ");
  }
  return "";
}
function _temp1(label) {
  return label.id;
}
function _temp0(params_5) {
  return <MarkupTags markup={params_5.row.entity} />;
}
function _temp9(__3, row_3) {
  return getMarkupStatusValue(row_3.entity.status?.value);
}
function _temp8(params_4) {
  return <MarkupStatusDropdown markup={params_4.row.entity} />;
}
function _temp7(__2, row_2) {
  return row_2.entity.dueDate?.value ? new Date(row_2.entity.dueDate.value) : undefined;
}
function _temp6(params_3) {
  return <MarkupDueDate markup={params_3.row.entity} />;
}
function _temp5(params_2) {
  return <MarkupCreator markup={params_2.row.entity} />;
}
function _temp4(__0, row_0) {
  return row_0.entity.assignee?.values?.[0];
}
function _temp3(params_1) {
  return <MarkupAssignee markup={params_1.row.entity} />;
}
function _temp2(_, row) {
  return row.entity.name;
}
function _temp(params_0) {
  return <MarkupNameAndDetails markup={params_0.row.entity} />;
}